import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useAuthGateState from '../../../hooks/useAuthGateState';
import { SESSION_STORAGE_TOKEN_KEY } from '../../../constants';
import { cleanFullName } from '../../../utils/name';

type PortalException = {
  id?: string;
  object?: string;
  exception_type?: string;
  exceptionType?: string;
  expires_at?: string;
  expiresAt?: string;
  dl_required?: boolean;
  dlRequired?: boolean;
  previous_dl_required?: boolean;
  previousDlRequired?: boolean;
  completed_at?: string;
  completedAt?: string;
};

type PortalReport = {
  id?: string;
  candidate_id?: string;
  candidateId?: string;
  account_id?: string;
  accountId?: string;
  account_name?: string;
  accountName?: string;
  status?: string;
  display_status?: string;
  displayStatus?: string;
  type?: string;
  completed_date?: string;
  completedDate?: string;
  exceptions?: PortalException[];
};

type MappedException = {
  type?: string;
  object?: string;
  id?: string;
  url?: string;
  expiresAt?: string;
};

export type ChatMetadataState = {
  portal?: {
    firstName?: string | null;
    country?: string | null;
    count?: number;
    token?: string | null;
    termsOfService?: { accepted?: boolean };
    reports?: PortalReport[];
  };
  privacy?: { contact_name?: string | null };
  candidateLogin?: {
    candidateEmail?: string;
    authMethod?: string | null;
  };
};

export type ChatMetadataSession = {
  candidateIds: string | null;
  userAccessToken: string | null;
};

export type ChatMetadataFields = {
  cxCandidateIds: string | null;
  cxCountry: string | null;
  cxAuthMethod: string | null;
  cxReportCount: string;
  cxReportIds: string;
  cxReportStatuses: string;
  cxReportDisplayStatuses: string;
  cxAccountNames: string;
  cxHasActionRequired: string;
  cxHasExceptions: string;
  cxTosAccepted: string;
  cxReports: string;
  cxExceptionTypes: string;
  cxExceptionCount: string;
  cxExceptionsExpiringSoon: string;
  cxUserAccessToken: string | null;
  cxFirstName: string | null;
  cxFullName: string | null;
  cxEmail: string | null;
};

// Every field must be classified here: sensitive fields hold credentials or
// PII and go to Ada's sensitiveMetaFields instead of its plain metaFields.
export const CHAT_METADATA_SENSITIVITY: Record<
  keyof ChatMetadataFields,
  boolean
> = {
  cxCandidateIds: false,
  cxCountry: false,
  cxAuthMethod: false,
  cxReportCount: false,
  cxReportIds: false,
  cxReportStatuses: false,
  cxReportDisplayStatuses: false,
  cxAccountNames: false,
  cxHasActionRequired: false,
  cxHasExceptions: false,
  cxTosAccepted: false,
  cxReports: false,
  cxExceptionTypes: false,
  cxExceptionCount: false,
  cxExceptionsExpiringSoon: false,
  cxUserAccessToken: true,
  cxFirstName: true,
  cxFullName: true,
  cxEmail: true,
};

const SENSITIVE_CHAT_METADATA_KEYS = new Set<string>(
  Object.entries(CHAT_METADATA_SENSITIVITY)
    .filter(([, sensitive]) => sensitive)
    .map(([key]) => key),
);

export const isSensitiveChatMetadataKey = (key: string): boolean =>
  SENSITIVE_CHAT_METADATA_KEYS.has(key);

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const exceptionType = (e: PortalException): string | undefined =>
  e.exception_type ||
  e.exceptionType ||
  e.object ||
  (e.dl_required || e.dlRequired ? 'driver_license' : undefined) ||
  (e.previous_dl_required || e.previousDlRequired
    ? 'previous_driver_license'
    : undefined);

const exceptionExpiresAt = (e: PortalException): string | undefined =>
  e.expires_at || e.expiresAt;

const exceptionCompletedAt = (e: PortalException): string | undefined =>
  e.completed_at || e.completedAt;

const isExceptionUnexpired = (e: PortalException): boolean => {
  const expiresAt = exceptionExpiresAt(e);
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() > Date.now();
};

const isExceptionActionable = (e: PortalException): boolean => {
  if (exceptionCompletedAt(e)) return false;
  return isExceptionUnexpired(e);
};

const mapException = (e: PortalException): MappedException => {
  const mapped: MappedException = {};
  const type = exceptionType(e);
  const expiresAt = exceptionExpiresAt(e);
  if (type) mapped.type = type;
  if (e.object) mapped.object = e.object;
  if (e.id) mapped.id = e.id;
  if (expiresAt) mapped.expiresAt = expiresAt;
  if (e.id && isExceptionActionable(e)) {
    mapped.url = `${globalThis.location.origin}/verifications/${e.id}`;
  }
  return mapped;
};

export const buildChatMetadata = (
  state: ChatMetadataState,
  sessionValues: ChatMetadataSession,
): ChatMetadataFields => {
  const { portal, privacy, candidateLogin } = state;
  const reports: PortalReport[] = portal?.reports || [];

  const allExceptions = reports.flatMap(r => r.exceptions || []);
  const uniqueExceptionTypes = Array.from(
    new Set(
      allExceptions.map(exceptionType).filter((t): t is string => Boolean(t)),
    ),
  );

  return {
    cxCandidateIds: sessionValues.candidateIds,
    cxCountry: portal?.country || null,
    cxAuthMethod: candidateLogin?.authMethod || null,
    cxReportCount: String(portal?.count ?? 0),
    cxReportIds: reports
      .map(r => r.id)
      .filter(Boolean)
      .join(','),
    cxReportStatuses: reports
      .map(r => r.status)
      .filter(Boolean)
      .join(','),
    cxReportDisplayStatuses: reports
      .map(r => r.display_status || r.displayStatus)
      .filter(Boolean)
      .join(','),
    cxAccountNames: reports
      .map(r => r.account_name || r.accountName)
      .filter(Boolean)
      .join(','),
    cxHasActionRequired: String(
      reports.some(
        r => (r.display_status || r.displayStatus) === 'actionRequired',
      ),
    ),
    cxHasExceptions: String(reports.some(r => (r.exceptions?.length || 0) > 0)),
    cxTosAccepted: String(portal?.termsOfService?.accepted ?? false),
    cxReports: JSON.stringify(
      reports.map(r => ({
        id: r.id,
        candidateId: r.candidate_id || r.candidateId,
        accountId: r.account_id || r.accountId,
        accountName: r.account_name || r.accountName,
        status: r.status,
        displayStatus: r.display_status || r.displayStatus,
        type: r.type,
        completedDate: r.completed_date || r.completedDate || null,
        exceptions: (r.exceptions || []).map(mapException),
      })),
    ),
    cxExceptionTypes: uniqueExceptionTypes.join(','),
    cxExceptionCount: String(allExceptions.length),
    cxExceptionsExpiringSoon: String(
      allExceptions.some(e => {
        if (!isExceptionActionable(e)) return false;
        const expiresAt = exceptionExpiresAt(e);
        if (!expiresAt) return false;
        const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
        return msUntilExpiry > 0 && msUntilExpiry <= SEVEN_DAYS_MS;
      }),
    ),
    cxUserAccessToken: sessionValues.userAccessToken,
    cxFirstName: portal?.firstName || null,
    cxFullName: cleanFullName(privacy?.contact_name),
    cxEmail: candidateLogin?.candidateEmail || null,
  };
};

export const useChatMetadata = (): ChatMetadataFields => {
  const reports = useSelector((state: any) => state.portal?.reports);
  const firstName = useSelector((state: any) => state.portal?.firstName);
  const country = useSelector((state: any) => state.portal?.country);
  const count = useSelector((state: any) => state.portal?.count);
  const tosAccepted = useSelector(
    (state: any) => state.portal?.termsOfService?.accepted,
  );
  const contactName = useSelector((state: any) => state.privacy?.contact_name);
  const {
    candidateLogin: { candidateEmail, authMethod },
  } = useAuthGateState();

  const candidateIds = globalThis.sessionStorage.getItem('candidate_ids');
  const userAccessToken = globalThis.sessionStorage.getItem(
    SESSION_STORAGE_TOKEN_KEY,
  );

  return useMemo(
    () =>
      buildChatMetadata(
        {
          portal: {
            firstName,
            country,
            count,
            termsOfService: { accepted: tosAccepted },
            reports,
          },
          privacy: { contact_name: contactName },
          candidateLogin: { candidateEmail, authMethod },
        },
        { candidateIds, userAccessToken },
      ),
    [
      reports,
      firstName,
      country,
      count,
      tosAccepted,
      contactName,
      candidateEmail,
      authMethod,
      candidateIds,
      userAccessToken,
    ],
  );
};
